class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.overlay = this.querySelector('.cnd__overlay');
    this.productEl = this.querySelector('#cart-notification-product');
    this.recommendationsEl = this.querySelector('#cart-notification-recommendations');
    this.footerEl = this.querySelector('#cart-notification-button');
    this.header = document.querySelector('sticky-header');

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    if (this.overlay) this.overlay.addEventListener('click', this.close.bind(this));
    this.querySelectorAll('.cnd__close').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );

  }

  open() {
    this.classList.add('active');
    this.notification.classList.add('animate', 'active');
    this.lockScroll();

    this.resetGalleryScroll();

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );
  }

  close() {
    this.classList.remove('active');
    this.notification.classList.remove('active');
    this.unlockScroll();

    removeTrapFocus(this.activeElement);
  }

  // Lock the background scroll while the drawer is open.
  //
  // `overflow: hidden` on <body> alone is NOT enough: on iOS Safari touch
  // scrolling still bleeds through to the page behind the drawer (a
  // long-standing WebKit quirk), which is exactly the mobile bottom-sheet
  // case here. The reliable cross-browser fix is to pin the body with
  // `position: fixed`, offset by the current scroll position, and restore
  // both on close. We keep the `overflow-hidden` class too for consistency
  // with the theme's other drawers and any styling that depends on it.
  //
  // Guarded with `scrollLocked` so a second open() (e.g. adding another item
  // while the drawer is already up) doesn't overwrite the saved position with
  // the pinned near-zero value and break the restore.
  lockScroll() {
    if (this.scrollLocked) return;
    this.scrollLocked = true;
    this.scrollLockY = window.scrollY || document.documentElement.scrollTop || 0;

    // Compensate for the vanishing scrollbar so the page behind doesn't shift
    // (only matters with classic/overlay-less scrollbars, e.g. Windows).
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    document.body.classList.add('overflow-hidden');
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollLockY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;
  }

  unlockScroll() {
    if (!this.scrollLocked) return;
    this.scrollLocked = false;

    document.body.classList.remove('overflow-hidden');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.style.paddingRight = '';

    window.scrollTo(0, this.scrollLockY || 0);
    this.scrollLockY = 0;
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      const target = document.getElementById(section.id);
      if (!target) return;
      target.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.bindGalleryWheelScroll();
    this.eagerLoadImages(this.productEl);

    this.loadRecommendations(parsedState);

    if (this.header) this.header.reveal();
    this.open();
  }

  resetGalleryScroll() {
    const slides = this.productEl && this.productEl.querySelector('.cnd-product__slides');
    if (slides) slides.scrollTo({ left: 0, behavior: 'instant' });
  }

  // The section markup keeps `loading="lazy"` (cart-notification-product
  // renders every cart line on normal page loads), but once the drawer is
  // open its off-screen images must not wait for horizontal scroll proximity.
  eagerLoadImages(root) {
    if (!root) return;
    root.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      img.loading = 'eager';
    });
  }


  // Adds drag-to-scroll + wheel-to-horizontal-scroll on the slide track.
  // Touch swipe already works natively via `overflow-x: auto`.
  bindGalleryWheelScroll() {
    const slides = this.productEl && this.productEl.querySelector('.cnd-product__slides');
    if (!slides || slides.dataset.cndScrollBound === '1') return;
    slides.dataset.cndScrollBound = '1';

    // Vertical wheel → horizontal scroll (desktop mouse without horizontal wheel).
    slides.addEventListener(
      'wheel',
      (e) => {
        if (e.deltaX !== 0) return; // trackpad horizontal swipe already works
        const max = slides.scrollWidth - slides.clientWidth;
        if (max <= 0) return;
        const goingForward = e.deltaY > 0 && slides.scrollLeft < max;
        const goingBack = e.deltaY < 0 && slides.scrollLeft > 0;
        if (!goingForward && !goingBack) return;
        e.preventDefault();
        slides.scrollLeft += e.deltaY;
      },
      { passive: false }
    );

    // Bring the slide whose left edge is nearest the viewport start flush to
    // it. ALL snapping is done here in JS — there is no CSS scroll-snap on
    // this track (see component-cart-notification.css). This is deliberate:
    // toggling `scroll-snap-type` on/off around a drag re-snaps at unreliable
    // moments (mid-animation, or not at all in browsers without `scrollend`),
    // which lands the track at arbitrary offsets and cuts the image. Driving
    // the snap ourselves always rests on a clean image edge.
    const snapToNearest = () => {
      const slideEls = slides.querySelectorAll('.cnd-product__slide');
      if (!slideEls.length) return;
      const containerLeft = slides.getBoundingClientRect().left;
      let bestDelta = 0;
      let bestDist = Infinity;
      slideEls.forEach((slide) => {
        const delta = slide.getBoundingClientRect().left - containerLeft;
        if (Math.abs(delta) < bestDist) {
          bestDist = Math.abs(delta);
          bestDelta = delta;
        }
      });
      if (Math.abs(bestDelta) < 1) return;
      slides.scrollTo({ left: slides.scrollLeft + bestDelta, behavior: 'smooth' });
    };

    // Snap once free scrolling (wheel / trackpad swipe) settles. Skipped while
    // a pointer drag is in progress — the drag handler snaps on release. The
    // programmatic snap above also fires 'scroll' events, but they resolve to
    // a zero delta (already aligned), so this never fights itself.
    let isDragging = false;
    let scrollIdleTimer = null;
    slides.addEventListener(
      'scroll',
      () => {
        if (scrollIdleTimer) clearTimeout(scrollIdleTimer);
        scrollIdleTimer = setTimeout(() => {
          scrollIdleTimer = null;
          if (!isDragging) snapToNearest();
        }, 110);
      },
      { passive: true }
    );

    // Click-and-drag to scroll; snaps to the nearest image edge on release.
    let dragStartX = 0;
    let dragStartScroll = 0;
    let dragMoved = false;

    const startDrag = (e) => {
      isDragging = true;
      dragMoved = false;
      dragStartX = e.pageX;
      dragStartScroll = slides.scrollLeft;
      slides.style.cursor = 'grabbing';
      slides.style.userSelect = 'none';
    };

    const endDrag = () => {
      if (!isDragging) return;
      isDragging = false;
      slides.style.cursor = '';
      slides.style.userSelect = '';
      snapToNearest();
    };

    slides.addEventListener('mousedown', startDrag);
    slides.addEventListener('mouseleave', endDrag);
    slides.addEventListener('mouseup', endDrag);
    slides.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      const delta = e.pageX - dragStartX;
      if (Math.abs(delta) > 3) dragMoved = true;
      slides.scrollLeft = dragStartScroll - delta;
      e.preventDefault();
    });
    // Prevent the image's default drag-ghost from interfering.
    slides.addEventListener('dragstart', (e) => e.preventDefault());
    // Swallow click after a drag so an image link isn't followed and the
    // click-to-navigate handler below doesn't fire on drop.
    slides.addEventListener(
      'click',
      (e) => {
        if (dragMoved) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      true
    );

    // Click on a slide → smooth-scroll it to the start of the viewport so
    // the next/previous image becomes the focused one. Drag-ended clicks
    // are swallowed by the capture-phase handler above, so this only fires
    // on real clicks.
    slides.addEventListener('click', (e) => {
      const slide = e.target.closest('.cnd-product__slide');
      if (!slide || !slides.contains(slide)) return;
      const slideRect = slide.getBoundingClientRect();
      const containerRect = slides.getBoundingClientRect();
      const delta = slideRect.left - containerRect.left;
      if (Math.abs(delta) < 1) return;
      slides.scrollTo({ left: slides.scrollLeft + delta, behavior: 'smooth' });
    });

    slides.style.cursor = 'grab';
  }

  loadRecommendations(parsedState) {
    if (!this.recommendationsEl) return;

    const url = this.resolveRecommendationsUrl(parsedState);
    if (!url) {
      // Couldn't determine a product handle: keep any previously rendered
      // recommendations visible rather than wiping them on a non-product add.
      return;
    }

    // Skip refetch when the source product hasn't changed — avoids the
    // flicker of re-rendering identical cards on repeated adds.
    if (this.recommendationsUrl === url && this.recommendationsEl.children.length > 0) {
      return;
    }

    this.recommendationsRequestId = (this.recommendationsRequestId || 0) + 1;
    const requestId = this.recommendationsRequestId;

    fetch(url)
      .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
      .then((html) => {
        if (requestId !== this.recommendationsRequestId) return;
        const inner = this.getSectionInnerHTML(html);
        if (inner && inner.trim().length > 0) {
          this.recommendationsEl.innerHTML = inner;
          this.recommendationsEl.hidden = false;
          this.recommendationsUrl = url;
          this.bindRecommendationCards();
          this.eagerLoadImages(this.recommendationsEl);
        } else {
          this.recommendationsEl.innerHTML = '';
          this.recommendationsEl.hidden = true;
          this.recommendationsUrl = null;
        }
      })
      .catch(() => {
        if (requestId !== this.recommendationsRequestId) return;
        // Don't wipe a previously rendered list on transient network errors.
        if (this.recommendationsEl.children.length === 0) {
          this.recommendationsEl.hidden = true;
        }
      });
  }

  resolveRecommendationsUrl(parsedState) {
    if (parsedState && typeof parsedState.url === 'string' && parsedState.url.indexOf('/products/') !== -1) {
      const path = parsedState.url.split('?')[0].split('#')[0];
      return `${path}?section_id=cart-notification-recommendations`;
    }
    const handle = this.resolveAddedProductHandle(parsedState);
    if (!handle) return null;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return `${root.replace(/\/$/, '')}/products/${handle}?section_id=cart-notification-recommendations`;
  }

  resolveAddedProductHandle(parsedState) {
    if (!parsedState) return null;
    if (parsedState.product_handle) return parsedState.product_handle;
    if (parsedState.handle) return parsedState.handle;
    if (typeof parsedState.url === 'string') {
      const match = parsedState.url.match(/\/products\/([^/?#]+)/);
      if (match) return match[1];
    }
    if (parsedState.items && this.cartItemKey) {
      const item = parsedState.items.find((it) => it.key === this.cartItemKey);
      if (item && item.handle) return item.handle;
    }
    return null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  bindRecommendationCards() {
    this.recommendationsEl.querySelectorAll('[data-cnd-reco]').forEach((card) => {
      if (card.dataset.cndRecoBound === '1') return;
      card.dataset.cndRecoBound = '1';
      new CartNotificationRecoCard(card, this);
    });
  }

  // Called after a recommended product is added. Refreshes only the footer
  // total and the cart bubble — the just-added product display stays put,
  // so the drawer doesn't visibly swap its hero image/title for the reco.
  refreshAfterAdd() {
    fetch(`${window.Shopify.routes.root || '/'}?sections=cart-notification-button,cart-icon-bubble`)
      .then((response) => (response.ok ? response.json() : Promise.reject(response.status)))
      .then((sections) => {
        const buttonHTML = sections['cart-notification-button'];
        const bubbleHTML = sections['cart-icon-bubble'];
        if (buttonHTML && this.footerEl) {
          this.footerEl.innerHTML = this.getSectionInnerHTML(buttonHTML);
        }
        if (bubbleHTML) {
          const target = document.getElementById('cart-icon-bubble');
          if (target) target.innerHTML = this.getSectionInnerHTML(bubbleHTML);
        }
      })
      .catch(() => {});
  }
}

customElements.define('cart-notification', CartNotification);

class CartNotificationRecoCard {
  constructor(root, host) {
    this.root = root;
    this.host = host;

    const dataNode = root.querySelector('script[data-cnd-reco-data]');
    try {
      this.data = JSON.parse(dataNode.textContent);
    } catch (e) {
      this.data = { variants: [], colorOptionIndex: 0, sizeOptionIndex: 0 };
    }

    this.priceEl = root.querySelector('[data-cnd-reco-price]');
    this.imageEl = root.querySelector('.cnd-recos__image');
    this.cta = root.querySelector('[data-cnd-reco-add]');
    this.colorBtns = Array.from(root.querySelectorAll('[data-cnd-reco-color]'));
    this.sizeBtns = Array.from(root.querySelectorAll('[data-cnd-reco-size]'));

    this.selectedColor = this.data.preselectedColor || null;
    this.selectedSize = null;
    this.added = false;

    this.colorBtns.forEach((btn) => btn.addEventListener('click', () => this.onColorClick(btn)));
    this.sizeBtns.forEach((btn) => btn.addEventListener('click', () => this.onSizeClick(btn)));
    if (this.cta) this.cta.addEventListener('click', () => this.onAddClick());

    this.refreshSizeAvailability();
    this.refreshCta();
    this.bindSizesDragScroll();
  }

  // Click-and-drag to scroll the size carousel. Horizontal scroll isn't
  // discoverable with a desktop mouse (no horizontal wheel), so a grab-drag
  // affordance is needed; touch already swipes natively via `overflow-x: auto`.
  // Snap is disabled mid-drag so CSS scroll-snap doesn't fight the pointer,
  // then re-applies (proximity, gentle) on release. A drag that moved past a
  // few px swallows the trailing click so a chip isn't toggled on drop.
  bindSizesDragScroll() {
    const track = this.root.querySelector('.cnd-recos__sizes');
    if (!track || track.dataset.cndDragBound === '1') return;
    // Nothing to drag when the chips already fit.
    if (track.scrollWidth <= track.clientWidth) return;
    track.dataset.cndDragBound = '1';
    track.classList.add('is-draggable');

    let isDragging = false;
    let startX = 0;
    let startScroll = 0;
    let moved = false;

    const start = (e) => {
      isDragging = true;
      moved = false;
      startX = e.pageX;
      startScroll = track.scrollLeft;
      track.classList.add('is-dragging');
    };

    const end = () => {
      if (!isDragging) return;
      isDragging = false;
      track.classList.remove('is-dragging');
    };

    track.addEventListener('mousedown', start);
    track.addEventListener('mouseleave', end);
    track.addEventListener('mouseup', end);
    track.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      const delta = e.pageX - startX;
      if (Math.abs(delta) > 3) moved = true;
      track.scrollLeft = startScroll - delta;
      e.preventDefault();
    });
    track.addEventListener('dragstart', (e) => e.preventDefault());
    // Capture phase: kill the click that ends a real drag before it reaches
    // the chip's own click handler.
    track.addEventListener(
      'click',
      (e) => {
        if (moved) {
          e.preventDefault();
          e.stopPropagation();
          moved = false;
        }
      },
      true
    );
  }

  variantsForColor(color) {
    if (this.data.colorOptionIndex === 0) return [];
    const idx = this.data.colorOptionIndex - 1;
    return this.data.variants.filter((v) => v.options && v.options[idx] === color);
  }

  updateImageForColor(color) {
    if (!this.imageEl || !color) return;
    const candidates = this.variantsForColor(color);
    if (candidates.length === 0) return;
    const variant = candidates.find((v) => v.available && v.image) || candidates.find((v) => v.image);
    if (!variant || !variant.image) return;
    if (variant.image.src) this.imageEl.src = variant.image.src;
    if (variant.image.srcset) this.imageEl.srcset = variant.image.srcset;
    if (variant.image.alt) this.imageEl.alt = variant.image.alt;
  }

  variantFor(color, size) {
    return this.data.variants.find((v) => {
      const opts = v.options || [];
      const colorMatch = this.data.colorOptionIndex === 0 || opts[this.data.colorOptionIndex - 1] === color;
      const sizeMatch = this.data.sizeOptionIndex === 0 || opts[this.data.sizeOptionIndex - 1] === size;
      return colorMatch && sizeMatch;
    });
  }

  onColorClick(btn) {
    if (this.added) return;
    const value = btn.getAttribute('data-cnd-reco-color');
    this.selectedColor = value;
    this.colorBtns.forEach((b) => b.setAttribute('aria-pressed', b === btn ? 'true' : 'false'));

    this.updateImageForColor(value);

    if (this.selectedSize) {
      const v = this.variantFor(this.selectedColor, this.selectedSize);
      if (!v || !v.available) {
        this.selectedSize = null;
        this.sizeBtns.forEach((b) => b.setAttribute('aria-pressed', 'false'));
      }
    }

    this.refreshSizeAvailability();
    this.refreshCta();
  }

  onSizeClick(btn) {
    if (this.added) return;
    if (btn.disabled) return;
    const value = btn.getAttribute('data-cnd-reco-size');
    this.selectedSize = value;
    this.sizeBtns.forEach((b) => b.setAttribute('aria-pressed', b === btn ? 'true' : 'false'));
    this.refreshCta();
  }

  refreshSizeAvailability() {
    this.sizeBtns.forEach((btn) => {
      const size = btn.getAttribute('data-cnd-reco-size');
      const v = this.variantFor(this.selectedColor, size);
      const available = v && v.available;
      btn.disabled = !available;
      btn.setAttribute('aria-disabled', String(!available));
    });
  }

  refreshCta() {
    if (!this.cta) return;
    if (this.added) {
      this.cta.disabled = true;
      this.cta.setAttribute('aria-disabled', 'true');
      this.cta.textContent = this.localized('added', 'Aggiunto!');
      return;
    }
    const v = this.selectedSize ? this.variantFor(this.selectedColor, this.selectedSize) : null;
    const ready = v && v.available;
    this.cta.disabled = !ready;
    this.cta.setAttribute('aria-disabled', String(!ready));
    this.cta.textContent = ready
      ? this.localized('add', 'Aggiungi al carrello')
      : this.localized('select_size', 'Seleziona la taglia');
  }

  localized(key, fallback) {
    const dict = (window.cartNotificationStrings || {});
    return dict[key] || fallback;
  }

  onAddClick() {
    const v = this.selectedSize ? this.variantFor(this.selectedColor, this.selectedSize) : null;
    if (!v || !v.available || this.added) return;

    this.cta.disabled = true;
    this.cta.setAttribute('aria-busy', 'true');

    const body = new FormData();
    body.append('id', v.id);
    body.append('quantity', '1');

    fetch(`${window.Shopify.routes.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body,
    })
      .then((res) => (res.ok ? res.json() : res.json().then((j) => Promise.reject(j))))
      .then((addedItem) => {
        this.added = true;
        this.host.lastAddedItemKey = addedItem.key;
        this.cta.removeAttribute('aria-busy');
        this.refreshCta();
        this.host.refreshAfterAdd();
      })
      .catch(() => {
        this.cta.removeAttribute('aria-busy');
        this.refreshCta();
      });
  }
}
